import {
  UserRouteActionV1Model,
  getHandledJobUids
} from '@core/models/entities/implementations/user-route-action-v1';
import { UserRouteV1Model } from '@core/models/entities/implementations/user-route-v1';
import { parseISO8601Duration } from '@core/utils/time-utils';

type Steps = UserRouteActionV1Model['steps_from_previous_finish'];

function stepsMs(steps: Steps): number {
  return steps.reduce(
    (total, step) => total + parseISO8601Duration(step.duration).totalMilliseconds,
    0
  );
}

const INSIDE_TIME_WINDOW_KINDS: readonly string[] = [
  'InsideSetup',
  'WaitDuringService',
  'PauseDuringService',
  'ActiveService',
  'InsideSetdown'
];

/** The steps that run before the action's first step inside its time window. */
export function stepsBeforeTimeWindow(action: UserRouteActionV1Model): Steps {
  const steps = action.steps_from_previous_finish;
  const first = steps.findIndex((step) => INSIDE_TIME_WINDOW_KINDS.includes(step.kind));
  return first < 0 ? [] : steps.slice(0, first);
}

export function actionStart(action: UserRouteActionV1Model): Date {
  return new Date(action.scheduled_first_step_start_datetime);
}

export function actionFinish(action: UserRouteActionV1Model): Date {
  return new Date(
    actionStart(action).getTime() + stepsMs(action.steps_from_previous_finish)
  );
}

/** The instant a pin is measured from: where the action's first inside-time-window step starts. */
export function pinOrigin(action: UserRouteActionV1Model): Date {
  return new Date(actionStart(action).getTime() + stepsMs(stepsBeforeTimeWindow(action)));
}

/**
 * A stop an edit placed and no recompute has timed yet: DL leaves it no step inside its time
 * window, so its start and finish are placeholders.
 */
export function awaitsTiming(action: UserRouteActionV1Model): boolean {
  return (
    getHandledJobUids(action).length > 0 &&
    !action.steps_from_previous_finish.some((step) =>
      INSIDE_TIME_WINDOW_KINDS.includes(step.kind)
    )
  );
}

/**
 * The earliest instant a stop can be pinned to, as DL bounds it: the nearest timed action before
 * it, which may be the route's start leg, has to finish and the stop's own steps before its time
 * window have to elapse, bar its waits, which an earlier pin absorbs. `null` when no timed action
 * precedes it.
 */
export function earliestPinStart(route: UserRouteV1Model, actionUid: string): Date | null {
  const index = route.actions.findIndex((action) => action.uid === actionUid);
  const predecessor = route.actions
    .slice(0, Math.max(index, 0))
    .reverse()
    .find((action) => !awaitsTiming(action));
  if (!predecessor) {
    return null;
  }
  return new Date(
    actionFinish(predecessor).getTime() +
      stepsMs(
        stepsBeforeTimeWindow(route.actions[index]).filter((step) => step.kind !== 'Wait')
      )
  );
}

/**
 * The action whose start is the route's departure, which a route start moves: its first, whether a
 * start leg or, on a vehicle with no start site, a stop that its start setup opens. `null` on a
 * route with no actions yet.
 */
export function departureAction(route: UserRouteV1Model): UserRouteActionV1Model | null {
  return route.actions[0] ?? null;
}

/** The route's job-less first action. `null` when the first action serves a job. */
export function startLeg(route: UserRouteV1Model): UserRouteActionV1Model | null {
  const first = route.actions[0];
  return first && getHandledJobUids(first).length === 0 ? first : null;
}

/**
 * The route's job-less last action, its return to the vehicle's end site. `null` when the last
 * action serves a job, the vehicle having no end site, or when it is the route's only action,
 * which `startLeg` already names.
 */
export function finishLeg(route: UserRouteV1Model): UserRouteActionV1Model | null {
  const actions = route.actions;
  const last = actions.at(-1);
  return actions.length > 1 && last && getHandledJobUids(last).length === 0 ? last : null;
}

/** The route's actions between its start and finish legs, each of which it may lack. */
export function routeStops(route: UserRouteV1Model): UserRouteActionV1Model[] {
  const legs = new Set([startLeg(route), finishLeg(route)]);
  return route.actions.filter((action) => !legs.has(action));
}
